import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ShieldAlert, ArrowLeft } from "lucide-react";
import useBrand, { BrandMark } from "@/hooks/useBrand";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = useMemo(() => params.get("token") || "", [params]);
  const nav = useNavigate();
  const brand = useBrand();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) {
      toast.error("Password baru minimal 8 karakter");
      return;
    }
    if (pw !== pw2) {
      toast.error("Konfirmasi password tidak cocok");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset", { token, new_password: pw });
      setDone(true);
      toast.success("Password berhasil direset");
      setTimeout(() => nav("/login", { replace: true }), 1500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Gagal reset password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen brand-hero flex items-center justify-center px-6">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <BrandMark brand={brand} size={52} />
          <div>
            <div className="font-extrabold text-2xl tracking-tight text-slate-900">{brand.company_name || "Gogojalan"}</div>
            <div className="text-xs text-slate-500">Buat password baru</div>
          </div>
        </div>

        <div className="paper p-8">
          {!token ? (
            <div className="text-center space-y-4" data-testid="reset-invalid-panel">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-red-100">
                <ShieldAlert size={28} className="text-red-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Link tidak valid</h1>
              <p className="text-sm text-slate-500">Link reset password tidak lengkap. Silakan minta ulang.</p>
              <Link to="/forgot-password" className="text-sm text-[#556b2f] hover:underline inline-flex items-center gap-1 justify-center">
                Minta link baru
              </Link>
            </div>
          ) : done ? (
            <div className="text-center space-y-4" data-testid="reset-done-panel">
              <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#e4ecc9]">
                <CheckCircle2 size={28} className="text-[#556b2f]" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">Password diperbarui</h1>
              <p className="text-sm text-slate-500">Kami akan arahkan ke halaman login…</p>
            </div>
          ) : (
            <>
              <h1 className="text-2xl font-bold text-slate-900 mb-1">Buat password baru</h1>
              <p className="text-sm text-slate-500 mb-6">Minimal 8 karakter. Link ini berlaku selama 15 menit.</p>

              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label>Password baru</Label>
                  <Input
                    data-testid="reset-password"
                    type="password"
                    autoComplete="new-password"
                    value={pw}
                    onChange={(e) => setPw(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <div>
                  <Label>Konfirmasi password baru</Label>
                  <Input
                    data-testid="reset-password-confirm"
                    type="password"
                    autoComplete="new-password"
                    value={pw2}
                    onChange={(e) => setPw2(e.target.value)}
                    placeholder="••••••••"
                    required
                  />
                </div>
                <Button
                  data-testid="reset-submit"
                  type="submit"
                  disabled={busy}
                  className="w-full bg-[#556b2f] hover:bg-[#3d4d1e] h-11 text-base"
                >
                  {busy ? "Menyimpan..." : "Simpan password baru"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <Link
                  to="/login"
                  className="text-sm text-[#556b2f] hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft size={14} /> Kembali ke login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
